import { DEFAULT_STORE_PRIVACY } from './storePrivacy';
import { KreatorPrivacySection } from './KreatorPrivacySection';
import { useState } from 'react';
import { Link as LinkIcon, Sliders, Settings2, Layout } from 'lucide-react';
import type { KreatorSettingsData } from '../KreatorPage';
import { SectionHeader } from '../../components/SectionHeader';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { Slider, BreakpointSlider } from '../../components/ui/Slider';
import { Toggle } from '../../components/ui/Toggle';
import { createTenantKreatorAdapter, type KreatorApiAdapter } from '../../lib/kreator-api-adapter';
import { GLOBAL_LINK_FIELDS, emptyGlobalLinks, isValidGlobalLink } from './globalLinks';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  apiAdapter?: KreatorApiAdapter;
  embedded?: boolean;
}

const LOADING_ANIMATIONS = [
  { value: 'none', label: 'Brak' },
  { value: 'fade', label: 'Fade (zanikanie)' },
  { value: 'slide', label: 'Slide (przesunięcie)' },
  { value: 'spinner', label: 'Spinner (kółko)' },
];

export function KreatorSettingsSection({ integrationId, settings, onSaved, showError, apiAdapter, embedded = false }: Props) {
  const adapter = apiAdapter ?? createTenantKreatorAdapter(integrationId);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [form, setForm] = useState(() => {
    // Migrate columnGap: number → { desktop, tablet, mobile }
    const rawGap = settings?.settings.columnGap;
    const columnGap: { desktop: number; tablet: number; mobile: number } = typeof rawGap === 'number'
      ? { desktop: rawGap, tablet: rawGap, mobile: rawGap }
      : typeof rawGap === 'object' && rawGap !== null
        ? rawGap as { desktop: number; tablet: number; mobile: number }
        : { desktop: 32, tablet: 20, mobile: 10 };
    return {
      maxContainerWidth: settings?.settings.maxContainerWidth || 1440,
      pageLoadingAnimation: settings?.settings.pageLoadingAnimation || 'fade',
      borderStyle: settings?.settings.borderStyle || 'rounded' as 'rounded' | 'square',
      borderRadius: settings?.settings.borderRadius ?? 8,
      columnGap,
      customCss: settings?.settings.customCss || '',
      customJs: settings?.settings.customJs || '',
      containerPadding: {
        desktop: 20,
        tablet: 20,
        mobile: 15,
        ...(settings?.settings.containerPadding ?? {}),
      },
      noBgZeroPadding: ((settings?.settings as Record<string, unknown>)?.noBgZeroPadding as boolean) ?? false,
      iconStrokeWidth: ((settings?.settings as Record<string, unknown>)?.iconStrokeWidth as number) ?? 2,
      iconDrawEffect: ((settings?.settings as Record<string, unknown>)?.iconDrawEffect as boolean) ?? false,
      iconDrawDuration: ((settings?.settings as Record<string, unknown>)?.iconDrawDuration as number) ?? 0.8,
      privacy: { ...DEFAULT_STORE_PRIVACY, ...(settings?.settings.privacy ?? {}) },
      globalLinks: { ...emptyGlobalLinks(), ...(settings?.settings.globalLinks ?? {}) },
    };
  });

  const handleSave = async () => {
    const invalidLink = GLOBAL_LINK_FIELDS.find(field => !isValidGlobalLink(form.globalLinks[field.key]));
    if (invalidLink) {
      showError(`Nieprawidłowy URL w polu "${invalidLink.label}". Użyj pełnego adresu https:// albo ścieżki zaczynającej się od /`);
      return;
    }

    if (form.privacy.enabled && (!form.globalLinks.privacy_url || !form.globalLinks.cookie_url)) {
      showError('Przed włączeniem bannera uzupełnij linki do polityki prywatności i cookies.');
      return;
    }
    if (!Number.isInteger(form.privacy.retentionDays) || form.privacy.retentionDays < 1 || form.privacy.retentionDays > 180
      || ['title', 'message', 'analyticsDescription', 'marketingDescription'].some(key => !String(form.privacy[key as keyof typeof form.privacy]).trim())) {
      showError('Uzupełnij treści bannera i wybierz okres od 1 do 180 dni.');
      return;
    }
    setSaving(true);
    try {
      await adapter.updateSettings(form);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
      if (adapter.isTenantScoped) {
        try {
          await adapter.purgeWpCache();
        } catch (purgeErr: unknown) {
          const msg = (purgeErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
          try {
            const diag = await adapter.diagnoseWp();
            showError(diag.data.ok ? 'Zapisano, ale cache nie został wyczyszczony. Wyczyść cache ręcznie w WordPress.' : `Zapisano, ale cache WP nie działa: ${diag.data.error}`);
          } catch {
            showError(msg ? `Zapisano, ale czyszczenie cache nie powiodło się: ${msg}` : 'Zapisano ustawienia, ale nie udało się wyczyścić cache WordPress.');
          }
        }
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as { message?: string })?.message
        ?? 'Nie udało się zapisać ustawień';
      showError(msg);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Sliders className="w-6 h-6 text-accent-primary" />
            Ustawienia
          </h1>
          <p className="text-text-secondary mt-1">Podstawowe ustawienia wyglądu sklepu</p>
        </div>
      )}

      {/* Layout */}
      <div className="card">
        <SectionHeader
          icon={Layout}
          title="Układ i kontener"
          description="Szerokość kontenera, wcięcia i domyślny odstęp między kolumnami"
        />
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Maksymalna szerokość kontenera (px)
            </label>
            <input
              type="number"
              min="800"
              max="2400"
              value={form.maxContainerWidth}
              onChange={(e) => setForm({ ...form, maxContainerWidth: parseInt(e.target.value) || 1440 })}
              className="input w-32"
            />
            <p className="text-xs text-text-muted mt-1">Zalecane: 1200-1440px</p>
          </div>

          <BreakpointSlider
            label="Wcięcie kontenera (lewo/prawo)"
            description="Odstęp treści od krawędzi strony. Dotyczy wszystkich sekcji."
            value={form.containerPadding}
            onChange={(v) => setForm({ ...form, containerPadding: v })}
            min={0}
            max={100}
          />

          <div>
            <Toggle
              label="Zeruj padding sekcji bez tła"
              description="Sekcje bez tła mają 0 wewnętrznego paddingu — treść sięga do krawędzi sekcji."
              checked={form.noBgZeroPadding}
              onChange={(e) => setForm({ ...form, noBgZeroPadding: e.target.checked })}
            />
          </div>

          <BreakpointSlider
            label="Domyślny odstęp między kolumnami"
            description="Domyślny odstęp w siatce produktów i kreatorze stron"
            value={form.columnGap}
            onChange={(v) => setForm({ ...form, columnGap: v })}
            min={0}
            max={60}
          />

        </div>
      </div>

      {/* Style */}
      <div className="card">
        <SectionHeader
          icon={Settings2}
          title="Styl i animacje"
          description="Animacja ładowania, styl krawędzi i ikony"
          accent="secondary"
        />
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Animacja ładowania stron
            </label>
            <select
              value={form.pageLoadingAnimation}
              onChange={(e) => setForm({ ...form, pageLoadingAnimation: e.target.value })}
              className="input w-full max-w-xs"
            >
              {LOADING_ANIMATIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Styl krawędzi</label>
            <div className="flex gap-2">
              <button
                onClick={() => setForm({ ...form, borderStyle: 'rounded' })}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                  form.borderStyle === 'rounded'
                    ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
                    : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong'
                }`}
              >
                Zaokrąglone
              </button>
              <button
                onClick={() => setForm({ ...form, borderStyle: 'square' })}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                  form.borderStyle === 'square'
                    ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
                    : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong'
                }`}
              >
                Kwadratowe
              </button>
            </div>
            {form.borderStyle === 'rounded' && (
              <div className="mt-3">
                <Slider
                  label="Promień zaokrąglenia"
                  value={form.borderRadius}
                  onChange={(v) => setForm({ ...form, borderRadius: v })}
                  min={0}
                  max={30}
                  unit="px"
                />
              </div>
            )}
          </div>

          <div>
            <Slider
              label="Grubość ikon"
              value={form.iconStrokeWidth}
              onChange={(v) => setForm({ ...form, iconStrokeWidth: v })}
              min={1}
              max={2.5}
              step={0.25}
            />
            <p className="text-xs text-text-muted mt-1">
              Domyślna grubość linii dla wszystkich ikon Lucide w kreatorze. Każdy komponent Ikona może mieć własne nadpisanie.
            </p>
          </div>

          <div>
            <Toggle
              label="Efekt rysowania ikon"
              description="Ikony animowane przy wejściu w widok — wyglądają jakby same się rysowały. Pojedynczy komponent Ikona może mieć własne nadpisanie."
              checked={form.iconDrawEffect}
              onChange={(e) => setForm({ ...form, iconDrawEffect: e.target.checked })}
            />
            {form.iconDrawEffect && (
              <div className="mt-3 ml-12">
                <Slider
                  label="Czas rysowania"
                  value={form.iconDrawDuration}
                  onChange={(v) => setForm({ ...form, iconDrawDuration: v })}
                  min={0.3}
                  max={3}
                  step={0.1}
                  unit="s"
                />
              </div>
            )}
          </div>

        </div>
      </div>

      <KreatorPrivacySection templateSettings={settings?.settings} value={form.privacy} onChange={privacy => setForm({ ...form, privacy })} />

      {/* Global links */}
      <div className="card">
        <SectionHeader
          icon={LinkIcon}
          title="Globalne linki"
          description="Adresy stron prawnych i informacyjnych używane w stopce, formularzach i komunikatach"
          accent="tertiary"
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {GLOBAL_LINK_FIELDS.map(field => {
            const value = form.globalLinks[field.key];
            const invalid = !isValidGlobalLink(value);
            return (
              <div key={field.key}>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor={`global-link-${field.key}`}>
                  {field.label}
                </label>
                <input
                  id={`global-link-${field.key}`}
                  type="text"
                  value={value}
                  onChange={(event) => setForm({
                    ...form,
                    globalLinks: { ...form.globalLinks, [field.key]: event.target.value },
                  })}
                  placeholder={field.placeholder}
                  className={`input w-full ${invalid ? 'border-danger focus:border-danger' : ''}`}
                />
                <p className={`mt-1 text-xs ${invalid ? 'text-danger' : 'text-text-muted'}`}>
                  {invalid ? 'Użyj pełnego adresu https:// albo ścieżki zaczynającej się od /' : field.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <SettingsSaveBar saving={saving} status={saveStatus} onSave={handleSave} />
    </div>
  );
}
